import { useCallback, useReducer } from 'react';
import { produce } from 'immer';
import { Decimal } from 'decimal.js';
import { ValueDisplay, assetUtils } from '@zen/common-utils';
import { FpUtils } from '@zen/common-app-parts';
import oracles from '../../utils/oracles';

import {
  setter,
  setPrice,
  setTicker,
  setDate,
  setOracle,
  setCollateralAmount,
  setCollateralAsset,
  setAmount1,
  setAsset1,
  setAmount2,
  setAsset2,
  setResult,
} from './modules/setters';

const ACTIONS = {
  SET: 'SET',
  RESET: 'RESET',
};

const DEFAULT_VALUES_STATIC = {};

export default function useIssueCancelStore({ defaultValues = DEFAULT_VALUES_STATIC } = {}) {
  const [_state, dispatch] = useReducer(reducer, getInitialState(defaultValues));
  /** @type {import('./types').IssueCancelState} */
  const state = _state;

  const valid =
    state.date.valid &&
    state.oracle &&
    state.price.valid &&
    state.ticker.valid &&
    state.collateralAsset.valid;

  const validForm = valid && state.collateralAmount.valid;

  const oracleContractId = (state.oracle || {}).contractId;
  const oraclePubKey = (state.oracle || {}).publicKey;

  const generate = useCallback(() => {
    if (!validForm) return;

    const messageBody = FpUtils.getMessageBody({
      start: state.date.value.valueOf(),
      expiry: state.date.value.valueOf(),
      oracleContractId,
      oraclePubKey,
      price: new Decimal(state.price.value.safeValue).times(100).toString(),
      ticker: state.ticker.value,
      asset: assetUtils.getAssetFullId(state.collateralAsset.value),
    });

    dispatch({ type: ACTIONS.SET, payload: { prop: 'result', value: { messageBody } } });
  }, [
    oracleContractId,
    oraclePubKey,
    state.collateralAsset.value,
    state.date.value,
    state.price.value.safeValue,
    state.ticker.value,
    validForm,
  ]);

  return {
    state: { ...state, valid, validForm },
    actions: {
      setOracle: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'oracle', value } });
      }, []),
      setCollateralAsset: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'collateralAsset', value } });
      }, []),
      setCollateralAmount: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'collateralAmount', value } });
      }, []),
      setAsset1: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'asset1', value } });
      }, []),
      setAmount1: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'amount1', value } });
      }, []),
      setAsset2: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'asset2', value } });
      }, []),
      setAmount2: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'amount2', value } });
      }, []),
      setTicker: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'ticker', value } });
      }, []),
      setPrice: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'price', value } });
      }, []),
      setDate: useCallback((value) => {
        dispatch({ type: ACTIONS.SET, payload: { prop: 'date', value } });
      }, []),
      reset: useCallback(() => {
        dispatch({ type: ACTIONS.RESET, payload: { defaultValues } });
      }, [defaultValues]),
      generate,
    },
  };
}

const PropsSettersMap = {
  oracle: setOracle,
  collateralAsset: setCollateralAsset,
  collateralAmount: setCollateralAmount,
  asset1: setAsset1,
  amount1: setAmount1,
  asset2: setAsset2,
  amount2: setAmount2,
  ticker: setTicker,
  price: setPrice,
  date: setDate,
  result: setResult,
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case ACTIONS.SET:
      setter({ PropsSettersMap, draft, prop: action.payload.prop, value: action.payload.value });
      break;
    case ACTIONS.RESET:
      reset(draft, action.payload.defaultValues);
      break;
    default:
      throw new Error(`Unhandled action type ${action.type}`);
  }
});

function reset(draft, defaultValues) {
  setAmount1('', draft);
  setAsset1('', draft);
  setAmount2('', draft);
  setAsset2('', draft);
  setCollateralAmount('', draft);
  setCollateralAsset('', draft);
  setTicker('', draft);
  setPrice(ValueDisplay.create(), draft);
  setDate(null, draft);
  setResult({ error: '', messageBody: '' }, draft);

  setDefaultValues(draft, defaultValues);
}

/**
 * Set all default values in the initial state
 * @param {initialState} state
 * @param {Object} values
 */
function setDefaultValues(state, values) {
  if (values && Object.keys(values).length) {
    Object.keys(values).forEach((key) => {
      if (PropsSettersMap[key]) {
        PropsSettersMap[key](values[key], state);
      }
    });
  }
}

function getInitialState(defaultValues) {
  const initialState = {
    oracle: oracles[0],
    collateralAsset: {
      value: '',
      valid: false,
    },
    collateralAmount: {
      value: ValueDisplay.create(),
      valid: false,
    },
    asset1: {
      value: '',
      valid: false,
    },
    amount1: {
      value: ValueDisplay.create(),
      valid: false,
    },
    asset2: {
      value: '',
      valid: false,
    },
    amount2: {
      value: ValueDisplay.create(),
      valid: false,
    },
    ticker: {
      value: '',
      valid: false,
    },
    price: {
      value: ValueDisplay.create(),
      valid: false,
    },
    date: {
      value: null,
      valid: false,
    },
    result: {
      error: '',
      messageBody: '',
    },
  };

  setDefaultValues(initialState, defaultValues);
  return initialState;
}
